import type { RawTx } from "./api";

export type NodeKind = "address" | "contract";

export interface Account {
  address: string;
  stxBalance: number;
  totalTx: number;
  nonce: number;
  firstBlock: number;
  totalFees: number;
}

export interface Neighbor {
  id: string;
  kind: NodeKind;
  label: string;
  addr: string;
  tags: string[];
  count: number;
}

export interface TxRow {
  txid: string;
  dir: "IN" | "OUT";
  fn: string;
  amount: number;
  status: "SUCCESS" | "PENDING" | "FAILED";
  block: number;
  ts: number;
  ago: string;
  hash: string;
  fee: number;
  nonce: number;
  counterparty: string;
  type: string;
}

export interface NodeData {
  account: Account;
  neighbors: Neighbor[];
  txs: TxRow[];
}

export interface TxEventRow {
  type: string;
  detail: string;
  amount: string;
  color: string;
}

export interface TxDetail {
  txid: string;
  status: "SUCCESS" | "PENDING" | "FAILED";
  sender: string;
  recipient: string;
  fnLabel: string;
  amount: number;
  fee: number;
  nonce: number;
  block: number;
  confirmations: number;
  type: string;
  ago: string;
  events: TxEventRow[];
  eventCount: number;
  raw: string;
}

const MICRO = 1e6;

export function isContract(principal: string): boolean {
  return principal.includes(".");
}

export function contractName(principal: string): string {
  const dot = principal.indexOf(".");
  return dot >= 0 ? principal.slice(dot + 1) : principal;
}

export function shortAddr(a: string | undefined): string {
  if (!a) return "-";
  if (isContract(a)) {
    const [base, name] = a.split(".");
    return base.slice(0, 5) + "..." + (name ?? "");
  }
  return a.length > 13 ? a.slice(0, 6) + "..." + a.slice(-5) : a;
}

export function ago(ts: number): string {
  if (!ts) return "-";
  const s = Date.now() / 1000 - ts;
  if (s < 60) return "just now";
  if (s < 3600) return Math.floor(s / 60) + "m ago";
  if (s < 86400) return Math.floor(s / 3600) + "h ago";
  return Math.floor(s / 86400) + "d ago";
}

function mapStatus(s: string | undefined): TxRow["status"] {
  if (s === "success") return "SUCCESS";
  if (s === "pending") return "PENDING";
  return "FAILED";
}

function txAmount(tx: RawTx): number {
  const t = tx.token_transfer;
  if (t && t.amount) return Number(t.amount) / MICRO;
  return 0;
}

function counterpartyOf(tx: RawTx, principal: string): string {
  if (tx.tx_type === "contract_call" && tx.contract_call?.contract_id) {
    return tx.contract_call.contract_id;
  }
  if (tx.tx_type === "smart_contract" && tx.smart_contract?.contract_id) {
    return tx.smart_contract.contract_id;
  }
  if (tx.tx_type === "token_transfer") {
    const to = tx.token_transfer?.recipient_address ?? tx.token_transfer?.recipient ?? "";
    const from = tx.sender_address ?? "";
    return from === principal ? to : from;
  }
  const from = tx.sender_address ?? "";
  return from === principal ? "" : from;
}

function fnLabel(tx: RawTx): string {
  if (tx.tx_type === "contract_call") return tx.contract_call?.function_name ?? "contract-call";
  if (tx.tx_type === "token_transfer") return "transfer";
  if (tx.tx_type === "smart_contract") return "deploy-contract";
  if (tx.tx_type === "coinbase") return "coinbase";
  return tx.tx_type ?? "tx";
}

export function mapTxRows(txs: RawTx[], principal: string): TxRow[] {
  return txs.map((tx) => {
    const from = tx.sender_address ?? "";
    const dir: TxRow["dir"] = from === principal ? "OUT" : "IN";
    const ts = tx.burn_block_time ?? 0;
    const id = tx.tx_id ?? "";
    return {
      txid: id,
      dir,
      fn: fnLabel(tx),
      amount: txAmount(tx),
      status: mapStatus(tx.tx_status),
      block: tx.block_height ?? 0,
      ts,
      ago: ago(ts),
      hash: id ? "0x" + id.replace(/^0x/, "").slice(0, 7) : "0x-------",
      fee: tx.fee_rate ? Number(tx.fee_rate) / MICRO : 0,
      nonce: tx.nonce ?? 0,
      counterparty: shortAddr(counterpartyOf(tx, principal)),
      type: tx.tx_type ?? "tx",
    };
  });
}

export function buildNeighbors(txs: RawTx[], principal: string): Neighbor[] {
  const acc = new Map<string, Neighbor>();
  for (const tx of txs) {
    const cp = counterpartyOf(tx, principal);
    if (!cp || cp === principal) continue;
    const existing = acc.get(cp);
    if (existing) {
      existing.count += 1;
      continue;
    }
    const kind: NodeKind = isContract(cp) ? "contract" : "address";
    acc.set(cp, {
      id: cp,
      kind,
      label: kind === "contract" ? contractName(cp) : shortAddr(cp),
      addr: cp,
      tags: kind === "contract" ? ["CONTRACT"] : ["ADDRESS"],
      count: 1,
    });
  }
  return [...acc.values()].sort((a, b) => b.count - a.count);
}

export function buildAccount(
  principal: string,
  stxMicro: string | undefined,
  feesMicro: string | undefined,
  totalTx: number,
  nonce: number,
  txs: RawTx[],
): Account {
  const blocks = txs.map((t) => t.block_height ?? 0).filter((b) => b > 0);
  return {
    address: principal,
    stxBalance: stxMicro ? Number(stxMicro) / MICRO : 0,
    totalTx,
    nonce,
    firstBlock: blocks.length ? Math.min(...blocks) : 0,
    totalFees: feesMicro ? Number(feesMicro) / MICRO : 0,
  };
}

function eventRow(e: NonNullable<RawTx["events"]>[number]): TxEventRow {
  const type = e.event_type ?? "event";
  if (type === "stx_asset" && e.asset) {
    const amt = e.asset.amount ? Number(e.asset.amount) / MICRO : 0;
    return {
      type: "stx_" + (e.asset.asset_event_type ?? "transfer"),
      detail: shortAddr(e.asset.sender) + " -> " + shortAddr(e.asset.recipient),
      amount: amt ? amt.toFixed(6) + " STX" : "-",
      color: "#f5741e",
    };
  }
  if (type === "fungible_token_asset" && e.asset) {
    return {
      type: "ft_" + (e.asset.asset_event_type ?? "event"),
      detail: contractName(e.asset.asset_id ?? "token"),
      amount: e.asset.amount ?? "-",
      color: "#12a150",
    };
  }
  if (type === "non_fungible_token_asset" && e.asset) {
    return {
      type: "nft_" + (e.asset.asset_event_type ?? "event"),
      detail: contractName(e.asset.asset_id ?? "nft"),
      amount: e.asset.value?.repr ?? "-",
      color: "#d9631a",
    };
  }
  return {
    type: "contract_log",
    detail: e.contract_log?.value?.repr ?? "log",
    amount: "-",
    color: "rgba(23,19,16,.45)",
  };
}

export function buildTxDetail(tx: RawTx, chainTip: number): TxDetail {
  const sender = tx.sender_address ?? "";
  const recipient =
    tx.token_transfer?.recipient_address ??
    tx.token_transfer?.recipient ??
    tx.contract_call?.contract_id ??
    tx.smart_contract?.contract_id ??
    "";
  const block = tx.block_height ?? 0;
  const events = (tx.events ?? []).map(eventRow);
  const raw =
    tx.tx_type === "contract_call"
      ? "(contract-call? '" +
        (tx.contract_call?.contract_id ?? "") +
        "\n  " +
        (tx.contract_call?.function_name ?? "") +
        "\n  sender '" +
        sender +
        ")"
      : "(" + (tx.tx_type ?? "tx") + " sender '" + sender + " -> '" + recipient + ")";
  return {
    txid: tx.tx_id ?? "",
    status: mapStatus(tx.tx_status),
    sender,
    recipient,
    fnLabel: fnLabel(tx),
    amount: txAmount(tx),
    fee: tx.fee_rate ? Number(tx.fee_rate) / MICRO : 0,
    nonce: tx.nonce ?? 0,
    block,
    confirmations: chainTip && block ? Math.max(0, chainTip - block) : 0,
    type: tx.tx_type ?? "tx",
    ago: ago(tx.burn_block_time ?? 0),
    events,
    eventCount: tx.event_count ?? events.length,
    raw,
  };
}
